import { COMMA, ENTER, SPACE } from '@angular/cdk/keycodes';
import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, NavigationStart, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import {
  DynamicFormControlType,
  DynamicFormField,
} from '../shared/dynamic-form/dynamic-form.interfaces';
import { PhoneInputComponent } from '../shared/phone-input/phone-input.component';
import {
  BookingSource,
  Contact,
  FeatureFlagFieldType,
  FormFieldType,
  MeetingForm,
  NotificationType,
} from '@vendasta/meetings';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import { Observable, Subject, Subscription, throwError } from 'rxjs';
import { debounceTime, filter, first, map, takeUntil } from 'rxjs/operators';
import { BookMeNowStoreService } from '../book-me-now-store.service';
import {
  clearUniqueRequestId,
  resolveBookingSource,
} from '../booking-request-id';
import { TimeSlot } from '../bookme/bookme';
import { MeetingTypeForm, SelectField } from '../interface';
import {
  buildShortTimezoneDisplay,
  parsePhoneNumberToPhoneFields,
} from '../util';
import { ContactFormCacheService } from './contact-form-cache.service';
import { formFieldKeys, formKeys } from './interface';

@Component({
  selector: 'app-bookme-contact-form',
  templateUrl: './bookme-contact-form.component.html',
  styleUrls: ['./bookme-contact-form.component.scss'],
  standalone: false,
})
export class BookmeContactFormComponent implements OnInit, OnDestroy {
  metadata: { [key: string]: string | string[] };
  timeSlot$: Observable<TimeSlot>;
  timezoneOffset$: Observable<string>;
  meetingTypeId$: Observable<string>;
  timezoneDisplay$: Observable<string>;
  hideTitleText$: Observable<boolean>;
  form: UntypedFormGroup;
  submitted = false;
  displayDuplicateEmail = '';
  inviteeInputInvalid = false;

  showInviteeEmails = false;

  // Getter for email field dirty/touched state
  get isEmailFieldTouched(): boolean {
    return this.form.get('email').dirty || this.form.get('email').touched;
  }

  // Getter for email field invalid state
  get isEmailFieldInvalid(): boolean {
    return this.form.get('email').invalid;
  }

  // Getter for email field errors
  get emailFieldErrors(): ValidationErrors | null {
    return this.form.get('email').errors;
  }

  get isPhoneInvalid(): boolean {
    if (!this.galaxyPhoneInput?.internalControl) {
      return false;
    }
    return this.galaxyPhoneInput.internalControl.invalid;
  }

  separatorKeyCodes = [ENTER, COMMA, SPACE];
  // We allow pasted content separated by whitespace, newlines, or commas.
  pastedContentSeparatorRegexp = /[,\n\s]/g;
  isPhoneNumberRequired = false;
  isEmailRequired = false;

  inviteeEmails: string[] = [];
  meetingTypeForm: MeetingTypeForm;
  customFormFields: DynamicFormField[];

  submitting = false;
  isClientSiteMeeting = false;
  locationGuideLines = '';
  showSmsConsent = false;
  businessName = '';
  private bookingSourceParam = '';
  private isSmsFeatureFlagEnabled = false;

  @ViewChild('galaxyPhoneInput') galaxyPhoneInput: PhoneInputComponent;
  @ViewChild('additionalEmails')
  additionalEmailsInput: ElementRef<HTMLInputElement>;

  private readonly subscriptions: Subscription[] = [];
  private readonly destroy$ = new Subject<void>();
  private timeSlotSubscription?: Subscription;

  constructor(
    private readonly translate: TranslateService,
    public readonly bookmenowService: BookMeNowStoreService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly contactFormCacheService: ContactFormCacheService,
    private recaptchaV3Service: ReCaptchaV3Service,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    this.timezoneDisplay$ = this.bookmenowService.timezone$.pipe(
      map((timezone) => buildShortTimezoneDisplay(timezone))
    );
    this.timeSlot$ = this.bookmenowService.timeSlot$;
    this.goBackIfNoTimeSlotSelected(this.timeSlot$);
    this.timezoneOffset$ = this.bookmenowService.timezoneOffset$;
    this.meetingTypeId$ = this.bookmenowService.meetingTypeId$;

    // Get isEmailRequired and isPhoneNumberRequired from meeting type before setting up form
    const currentState = this.bookmenowService.getCurrentState();
    const meetingType = currentState?.meetingType;
    this.isEmailRequired = meetingType.isEmailRequired === true;
    this.isPhoneNumberRequired = meetingType.isPhoneNumberRequired === true;
    this.isClientSiteMeeting = this.bookmenowService.isClientSiteMeeting();
    this.locationGuideLines = this.bookmenowService.getLocationGuideLines();
    this.setupForm();
    this.hideTitleText$ = this.route.queryParams.pipe(
      map((params) => params?.hide_header === 'true')
    );

    // Show SMS consent disclosure whenever an SMS will actually be sent — i.e.
    // notificationType is SMS or BOTH. Hidden for email-only bookings.
    const notificationType = meetingType?.notificationType;
    this.showSmsConsent =
      notificationType === NotificationType.SMS ||
      notificationType === NotificationType.BOTH;

    // Get calendar display name for SMS consent text
    this.bookmenowService.calendar$
      .pipe(filter(Boolean), first(), takeUntil(this.destroy$))
      .subscribe((calendar) => {
        this.businessName = calendar.businessName || calendar.displayName || '';
      });

    // Check feature flag for SMS notification (used for confirmation page routing)
    const calendarId = this.bookmenowService.getCalendarId();
    this.bookmenowService
      .checkFeatureFlag({
        fieldType: FeatureFlagFieldType.FEATURE_FLAG_FIELD_TYPE_CALENDAR_ID,
        fieldValue: calendarId,
        featureFlagKey: 'meeting_scheduler_sms_notification',
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (isEnabled) => {
          this.isSmsFeatureFlagEnabled = isEnabled;
        },
        error: (err) => {
          console.error('Error checking feature flag:', err);
          this.isSmsFeatureFlagEnabled = false;
        },
      });
  }

  displayInviteeEmailField(): void {
    this.showInviteeEmails = true;
  }

  addInviteeEmail(value: string): void {
    const trimmedValue = value?.trim();
    if (!trimmedValue) {
      return;
    }

    const validationResult = this.validateAndCheckDuplicates([trimmedValue]);
    if (!validationResult.isValid) {
      this.handleValidationError(
        validationResult.errorEmail,
        validationResult.errorType
      );
      return;
    }

    this.addValidEmails([trimmedValue]);
  }

  onInviteeInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value?.trim();
    if (!value) {
      this.inviteeInputInvalid = false;
      this.displayDuplicateEmail = '';
      return;
    }
    this.inviteeInputInvalid = !this.isValidEmail(value);
    this.displayDuplicateEmail = this.inviteeInputInvalid ? value : '';
  }

  addInviteeEmailsFromPaste(event: ClipboardEvent | Event): void {
    let pastedText: string;
    if ((event as ClipboardEvent).clipboardData) {
      pastedText = (event as ClipboardEvent).clipboardData.getData('text');
    } else if (
      event &&
      event.target &&
      (event.target as HTMLInputElement).value
    ) {
      pastedText = (event.target as HTMLInputElement).value;
    } else {
      return;
    }

    if (!pastedText) {
      return;
    }

    // Parse and validate emails with custom validation
    const emails = this.parsePastedEmails(pastedText);
    if (emails.length === 0) {
      // No valid emails found, show error for first invalid email
      const firstInvalidEmail = pastedText
        .split(this.pastedContentSeparatorRegexp)
        .map((v) => v.trim())
        .find((v) => v && !this.isValidEmail(v));

      if (firstInvalidEmail) {
        this.displayDuplicateEmail = firstInvalidEmail;
        return;
      }
    }

    const validationResult = this.validateAndCheckDuplicates(emails);

    if (!validationResult.isValid) {
      this.handleValidationError(
        validationResult.errorEmail,
        validationResult.errorType,
        true
      );
      return;
    }

    this.addValidEmails(emails);
    this.setupPasteEventCleanup(emails);
  }

  removeInviteeEmail(event: string): void {
    if (!event) {
      return;
    }
    const value = (event as string).trim();
    this.inviteeEmails = this.inviteeEmails.filter((e) => e !== value);
  }

  onSubmit(): void {
    if (this.submitting) {
      return;
    }

    // Check if all emails are unique before submitting
    if (!this.areAllEmailsUnique()) {
      this.showDuplicateEmailNotification('', 'submit');
      return;
    }

    this.submitting = true;

    this.recaptchaV3Service
      .execute('bookMeeting')
      .pipe(first(), takeUntil(this.destroy$))
      .subscribe({
        next: (token) => {
          this.submit(token);
          this.submitting = false;
        },
        error: (err) => {
          this.submitting = false;
          return throwError(err);
        },
        complete: () => (this.submitting = false),
      })
      .add(() => (this.submitting = false));
  }
  private submit(recaptchaToken: string): void {
    // When phone number is required but untouched it doesn't get highlighted on submit
    // this line forces it to re-evaluate it and visually updates the valid status
    this.galaxyPhoneInput.internalControl.updateValueAndValidity();

    // TODO: For some reason the form is not always updated corresponding to its controls.
    // Here, Make sure the form value is up-to-date before checking its validity.
    const value = this.form.value;
    const countryCallingCode =
      this.galaxyPhoneInput.countryDetails.countryCallingCode.toString();
    const countryCode = this.galaxyPhoneInput.countryDetails.countryCode;
    Object.keys(this.form.controls).forEach((ctrl) => {
      if (ctrl === 'phoneNumber' && this.form.controls[ctrl].value) {
        // Get the phone number and remove any non-numeric characters
        let phoneNumber = this.form.controls[ctrl].value?.toString() || '';
        phoneNumber = phoneNumber.replace(/\D/g, '');

        // If the number starts with the country code, remove it
        if (phoneNumber.startsWith(countryCallingCode)) {
          phoneNumber = phoneNumber.substring(countryCallingCode.length);
        }

        value[ctrl] = `${countryCallingCode}-${phoneNumber}-${countryCode}`;
      } else {
        value[ctrl] = this.form.controls[ctrl].value;
      }
    });
    this.form.setValue(value);

    if (!this.form.valid) {
      return;
    }

    this.submitted = true;
    this.bookmenowService.setLocation(this.form.get('location')?.value);
    const calendarId = this.route.snapshot.params.calendarId;
    const meetingTypeId = this.route.snapshot.params.meetingTypeId;

    this.bookmenowService
      .bookMeeting(
        this.buildBookMeetingRequest(
          this.form,
          recaptchaToken,
          this.inviteeEmails
        )
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (meetingId) => {
          clearUniqueRequestId(
            meetingTypeId,
            this.bookmenowService.getMeetingTypeId()
          );
          if (this.isSmsFeatureFlagEnabled) {
            // Navigate to meeting-info page
            this.router.navigate(['/meeting-info', meetingId], {
              queryParamsHandling: 'merge',
            });
          } else {
            // Navigate to old confirmation page
            const confirmationParams = {
              meetingId,
              action: 'book',
            };

            this.router.navigate(
              ['/book', calendarId, meetingTypeId, 'confirmation'],
              {
                queryParamsHandling: 'merge',
                queryParams: confirmationParams,
              }
            );
          }
        },
        (err) => {
          console.error(err);
          if (err.status === 409) {
            this.bookmenowService.setErrorDisplay('conflict-booking');
            return;
          }
          this.bookmenowService.handleMeetingTypeError(err);
        }
      );
  }

  private async setupForm(): Promise<void> {
    this.metadata = Object.keys(this.route.snapshot.queryParams).reduce(
      (m, queryParam) => {
        if (
          queryParam &&
          (queryParam.startsWith('msm_') || queryParam.startsWith('utm_'))
        ) {
          m[queryParam] = this.route.snapshot.queryParams[queryParam];
        }
        return m;
      },
      {} as { [key: string]: string }
    );
    const cachedForm = this.contactFormCacheService.get();

    // Store bookingSource from query params or cache for later use
    this.bookingSourceParam =
      stringOrFirst(this.route.snapshot.queryParams['bookingSource']) ||
      cachedForm.bookingSource ||
      '';

    let phoneNumber =
      cachedForm.phoneNumber ||
      stringOrFirst(this.metadata[formKeys.phoneNumber]);

    this.form = new UntypedFormGroup({
      firstName: new UntypedFormControl(
        cachedForm.firstName ||
          stringOrFirst(this.metadata[formKeys.firstName]),
        [Validators.required, Validators.pattern(/\S/)]
      ),
      lastName: new UntypedFormControl(
        cachedForm.lastName || stringOrFirst(this.metadata[formKeys.lastName]),
        [Validators.required, Validators.pattern(/\S/)]
      ),
      phoneNumber: new UntypedFormControl(phoneNumber),
      email: new UntypedFormControl(
        cachedForm.email || stringOrFirst(this.metadata[formKeys.email]),
        this.isEmailRequired
          ? [Validators.required, customEmailValidator()]
          : [customEmailValidator()]
      ),
      inviteeEmails: new UntypedFormControl(''),
      comment: new UntypedFormControl(
        cachedForm.comment || stringOrFirst(this.metadata[formKeys.comment])
      ),
      location: new UntypedFormControl(
        cachedForm.location ||
          this.bookmenowService.getHostLocation() ||
          stringOrFirst(this.metadata[formKeys.location]),
        this.isClientSiteMeeting
          ? [Validators.required, Validators.pattern(/\S/)]
          : []
      ),
    });
    this.inviteeEmails = (
      cachedForm.inviteeEmails ||
      stringArray(this.metadata[formKeys.inviteeEmail]) ||
      []
    ).filter((v) => this.isValidEmail(v));
    if (this.inviteeEmails.length > 0) {
      this.showInviteeEmails = true;
    }
    this.meetingTypeForm = await this.bookmenowService.meetingTypeForm$
      .pipe(filter<MeetingTypeForm>(Boolean), first())
      .toPromise();

    // Update isEmailRequired and isPhoneNumberRequired if meeting type is now available
    const currentState = this.bookmenowService.getCurrentState();
    const meetingType = currentState?.meetingType;
    if (meetingType) {
      this.isEmailRequired = meetingType.isEmailRequired === true;
      this.isPhoneNumberRequired = meetingType.isPhoneNumberRequired === true;

      // Update email validators based on isEmailRequired
      const emailControl = this.form.get('email');
      if (emailControl) {
        const validators = this.isEmailRequired
          ? [Validators.required, customEmailValidator()]
          : [customEmailValidator()];
        emailControl.setValidators(validators);
        emailControl.updateValueAndValidity();
      }

      // Update phone number validators based on isPhoneNumberRequired
      const phoneControl = this.form.get('phoneNumber');
      if (phoneControl) {
        if (this.isPhoneNumberRequired) {
          phoneControl.setValidators([Validators.required]);
        } else {
          phoneControl.clearValidators();
        }
        phoneControl.updateValueAndValidity();
      }
    }

    const defaultValues = this.meetingTypeForm.fields.reduce((dv, field) => {
      const urlQueryParam = `msm_${field.id}`;
      return {
        ...dv,
        [field.id]: cachedForm[field.id] || this.metadata[urlQueryParam],
      };
    }, {});
    this.galaxyPhoneInput.setCountryCode(cachedForm.countryCode || 'US');
    const dynamicFields = meetingTypeFormToDynamicFields(
      this.meetingTypeForm,
      defaultValues
    );
    dynamicFields.forEach((field) => {
      this.form.addControl(field.id, field.formControl as UntypedFormControl);

      // Subscribe to value changes of each custom field to trigger form validation
      const subscription = field.formControl.valueChanges.subscribe(() => {
        this.form.updateValueAndValidity();
      });
      this.subscriptions.push(subscription);
    });
    this.customFormFields = dynamicFields;

    // Update form validity after adding custom controls
    this.form.updateValueAndValidity();

    this.subscriptions.push(
      this.form.valueChanges.subscribe((v) => {
        return this.contactFormCacheService.set({
          ...v,
          countryCode: this.galaxyPhoneInput.countryCode,
          inviteeEmails: this.inviteeEmails,
          bookingSource: this.bookingSourceParam,
        });
      })
    );
    this.subscriptions.push(
      this.router.events
        .pipe(filter((e) => e instanceof NavigationStart))
        .subscribe(() => {
          this.contactFormCacheService.set({
            ...this.form.value,
            countryCode: this.galaxyPhoneInput.countryCode,
            inviteeEmails: this.inviteeEmails,
            bookingSource: this.bookingSourceParam,
          });
        })
    );
    const countryCallingCode =
      this.galaxyPhoneInput.countryDetails.countryCallingCode.toString();
    // Format phone number from cache if it exists
    if (phoneNumber.startsWith(countryCallingCode)) {
      phoneNumber = phoneNumber.substring(countryCallingCode.length);
    }
    this.form.get('phoneNumber')?.setValue(phoneNumber);
  }

  private buildBookMeetingRequest(
    form: UntypedFormGroup,
    recaptchaToken: string,
    inviteeEmails?: string[]
  ): {
    calendarId: string;
    meetingTypeId: string;
    start: Date;
    comment: string;
    location: string;
    attendees: Contact[];
    metadata: { [key: string]: string };
    formAnswers: MeetingForm;
    recaptchaToken: string;
    bookingSource: BookingSource;
    hasSmsConsent?: boolean;
  } {
    const multiMetadata = Object.keys(this.metadata).reduce((m, key) => {
      if (formFieldKeys.indexOf(key) < 0) {
        m[key] = this.metadata[key];
      }
      return m;
    }, {} as { [key: string]: string | string[] });

    const metadata: { [key: string]: string } = Object.keys(
      multiMetadata
    ).reduce((acc, key) => {
      return { ...acc, [key]: stringOrFirst(multiMetadata[key]) };
    }, {});

    const customFields = this.meetingTypeForm.fields || [];
    customFields.forEach(
      (field) => (metadata[field.id] = this.form.get(field.id).value)
    );

    const bookingSource = resolveBookingSource();

    const meetingForm: MeetingForm = {
      formAnswers: customFields.map((field) => {
        return {
          id: field.id,
          type: field.type,
          label: field.label,
          answer: [this.form.get(field.id).value],
        };
      }),
    };

    const invitees = (inviteeEmails || []).map(
      (email) => new Contact({ email, isPrimary: false })
    );

    // Parse phone number to extract country calling code and national number
    const phoneFields = parsePhoneNumberToPhoneFields(
      form.get('phoneNumber').value
    );

    //
    return {
      calendarId: this.bookmenowService.getCalendarId(),
      meetingTypeId: this.bookmenowService.getMeetingTypeId(),
      start: this.bookmenowService.getSelectedTimeSlot().start,
      comment: this.form.get('comment').value,
      location: this.form.get(`location`).value,
      attendees: [
        new Contact({
          firstName: form.get('firstName').value,
          lastName: form.get('lastName').value,
          email: form.get('email').value,
          phoneFields: phoneFields,
          timeZone: {
            id: this.bookmenowService.getTimezone(),
          },
          isPrimary: true,
        }),
        ...invitees,
      ],
      metadata,
      formAnswers: meetingForm,
      recaptchaToken,
      bookingSource,
      hasSmsConsent: this.showSmsConsent ? true : undefined,
    };
  }

  private parsePastedEmails(pastedText: string): string[] {
    return pastedText
      .split(this.pastedContentSeparatorRegexp)
      .map((v) => v.trim())
      .filter((v) => v && this.isValidEmail(v));
  }

  private validateAndCheckDuplicates(emails: string[]): {
    isValid: boolean;
    errorEmail?: string;
    errorType?: 'invalid' | 'primary' | 'existing';
  } {
    const primaryEmail = this.form.get('email').value;

    // Check for invalid emails
    const invalidEmails = emails.filter((email) => !this.isValidEmail(email));
    if (invalidEmails.length > 0) {
      return {
        isValid: false,
        errorEmail: invalidEmails[0],
        errorType: 'invalid',
      };
    }

    // Check for duplicates against primary email
    const duplicateEmails = emails.filter((email) => email === primaryEmail);
    if (duplicateEmails.length > 0) {
      return {
        isValid: false,
        errorEmail: duplicateEmails[0],
        errorType: 'primary',
      };
    }

    // Check for duplicates against existing invitee emails
    const existingDuplicates = emails.filter(
      (email) => this.inviteeEmails.indexOf(email) >= 0
    );
    if (existingDuplicates.length > 0) {
      return {
        isValid: false,
        errorEmail: existingDuplicates[0],
        errorType: 'existing',
      };
    }

    return { isValid: true };
  }

  // Helper method to handle validation errors
  private handleValidationError(
    errorEmail: string,
    errorType: 'invalid' | 'primary' | 'existing',
    isPasteEvent = false
  ): void {
    this.displayDuplicateEmail = errorEmail;

    if (errorType === 'invalid') {
      // Don't clear input for invalid emails, let user see and correct
      return;
    }

    this.showDuplicateEmailNotification(errorEmail, errorType);

    if (isPasteEvent) {
      this.clearPastedInput();
    } else {
      this.resetInviteeEmailInput();
    }
  }

  // Helper method to add valid emails to the list
  private addValidEmails(emails: string[]): void {
    // Clear any duplicate error message
    this.displayDuplicateEmail = '';

    // Add the emails to the list
    this.inviteeEmails = [...(this.inviteeEmails || []), ...emails];
    this.inviteeInputInvalid = false;
    this.resetInviteeEmailInput();
  }

  // Clear the chip input after paste event is processed
  private setupPasteEventCleanup(_emails: string[]): void {
    requestAnimationFrame(() => {
      this.resetInviteeEmailInput();
    });
  }

  // Clear the chip input element after adding emails
  private resetInviteeEmailInput(): void {
    if (this.additionalEmailsInput?.nativeElement) {
      this.additionalEmailsInput.nativeElement.value = '';
    }
  }

  private showDuplicateEmailNotification(
    email: string,
    type: 'primary' | 'existing' | 'submit'
  ): void {
    let message: string;

    if (type === 'submit') {
      message =
        'Your primary email cannot be the same as any additional guest email';
    } else if (type === 'primary') {
      message = `"${email}" is the same as your primary email and was not added`;
    } else {
      message = `"${email}" is already in the guest list and was not added`;
    }

    this.snackBar.open(message, 'Dismiss', {
      duration: 5000, // 5 seconds
      panelClass: ['duplicate-email-notification'],
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
    });
  }

  private showInvalidEmailNotification(email: string): void {
    const message = `"${email}" is not a valid email address and was not added`;

    this.snackBar.open(message, 'Dismiss', {
      duration: 5000, // 5 seconds
      panelClass: ['duplicate-email-notification'],
      horizontalPosition: 'center',
      verticalPosition: 'bottom',
    });
  }

  private clearPastedInput(): void {
    requestAnimationFrame(() => {
      this.resetInviteeEmailInput();
    });
  }

  get isPrimaryEmailDuplicate(): boolean {
    const primaryEmail = this.form.get('email')?.value?.trim();
    if (!primaryEmail || this.inviteeEmails.length === 0) {
      return false;
    }
    return this.inviteeEmails.some((email) => email.trim() === primaryEmail);
  }

  isValidEmail(email: string): boolean {
    return customEmailValidator()(new UntypedFormControl(email)) === null;
  }

  areAllEmailsUnique(): boolean {
    const primaryEmail = this.form.get('email')?.value?.trim();
    if (!primaryEmail) {
      return true;
    }

    // Check if primary email matches any invitee email
    return !this.inviteeEmails.some((email) => email.trim() === primaryEmail);
  }

  private goBackIfNoTimeSlotSelected(
    timeSlot$: Observable<TimeSlot | null>
  ): void {
    this.timeSlotSubscription = timeSlot$
      .pipe(debounceTime(100), first())
      .subscribe((timeSlot) => {
        if (!timeSlot) {
          this.router.navigate(['../'], {
            relativeTo: this.route,
            queryParamsHandling: 'preserve',
          });
        }
      });
  }

  ngOnDestroy(): void {
    // Complete the destroy subject to trigger takeUntil on all subscriptions
    this.destroy$.next();
    this.destroy$.complete();

    // Unsubscribe from timeSlot subscription if it exists
    if (this.timeSlotSubscription) {
      this.timeSlotSubscription.unsubscribe();
    }

    // Fallback: unsubscribe from any remaining subscriptions
    (this.subscriptions || []).forEach((s) => s.unsubscribe());
  }
}

function stringOrFirst(s: string | string[]): string {
  if (!s) {
    return '';
  }
  if (Array.isArray(s)) {
    return s[0] || '';
  }
  return s;
}

function stringArray(s: string | string[]): string[] {
  if (!s) {
    return [];
  }
  if (Array.isArray(s)) {
    return s;
  }
  return [s];
}

export function customEmailValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const email = control.value?.trim();
    if (!email) {
      return null; // Empty is handled by Validators.required if needed
    }

    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email) ? null : { invalidEmail: true };
  };
}

function meetingTypeFormToDynamicFields(
  form: MeetingTypeForm,
  defaultValues: { [key: string]: string }
): DynamicFormField[] {
  if (!form || !form.fields || form.fields.length === 0) {
    return [];
  }
  return form.fields
    .filter((field) => field.type !== FormFieldType.FORM_FIELD_TYPE_INVALID)
    .map((field) => {
      const validators = [];
      if (field.required) {
        validators.push(Validators.required, Validators.pattern(/\S/));
      }
      if (field.validators && field.validators.length > 0) {
        validators.push(...field.validators);
      }

      const commonProps: Pick<
        DynamicFormField,
        'id' | 'label' | 'required' | 'formControl'
      > = {
        id: field.id,
        label: field.label,
        required: field.required,
        formControl: new UntypedFormControl(
          defaultValues[field.id],
          validators.length > 0 ? validators : null
        ),
      };
      switch (field.type) {
        case FormFieldType.FORM_FIELD_TYPE_SELECT:
          return {
            ...commonProps,
            type: DynamicFormControlType.SELECT,
            config: {
              ...commonProps,
              options: (field as SelectField).config.options,
            },
          } as DynamicFormField;
        case FormFieldType.FORM_FIELD_TYPE_TEXT:
          return {
            ...commonProps,
            type: DynamicFormControlType.INPUT,
          } as DynamicFormField;
        case FormFieldType.FORM_FIELD_TYPE_EMAIL: {
          // Add email validation
          const emailValidators = [...validators, customEmailValidator()];
          return {
            ...commonProps,
            type: DynamicFormControlType.EMAIL,
            formControl: new UntypedFormControl(
              defaultValues[field.id],
              emailValidators
            ),
          } as DynamicFormField;
        }
        case FormFieldType.FORM_FIELD_TYPE_PHONE_NUMBER:
          return {
            ...commonProps,
            type: DynamicFormControlType.PHONE,
          } as DynamicFormField;
      }
    });
}
